import React from 'react';
import moment from 'moment/moment';
import { blue } from '@mui/material/colors';
import { useWatch } from 'react-hook-form';
import { Chip, Stack, Typography } from '@mui/material';
import { Control } from 'react-hook-form/dist/types/form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import { TextFieldVariants, TextFieldProps } from '@mui/material/TextField/TextField';

import { DateField } from '../../rhf';
import { dateRangeConfiguration } from './periods';

interface Props<FormValues extends FieldValues = FieldValues> {
  size?: TextFieldProps['size'];
  variant?: TextFieldVariants;
  control: Control<FormValues>;
  nameTo: UseControllerProps<FormValues>['name'];
  nameFrom: UseControllerProps<FormValues>['name'];
  onRangeChange: (range: { endDate: moment.Moment; startDate: moment.Moment }) => Promise<void>;
}

export const DateRangeWithPeriods = <FormValues extends FieldValues = FieldValues>({
  nameTo,
  control,
  nameFrom,
  size = 'medium',
  variant = 'standard',
  onRangeChange,
}: Props<FormValues>) => {
  const currentTo = useWatch({ control, name: nameTo });
  const currentFrom = useWatch({ control, name: nameFrom });

  return (
    <Stack gap="0.5rem">
      <Stack direction="row" gap="1rem">
        <DateField name={nameFrom} label="From" control={control} slotProps={{ textField: { variant, size } }} />

        <DateField
          name={nameTo}
          label="To"
          control={control}
          maxDate={moment().endOf('year')}
          slotProps={{ textField: { variant, size } }}
        />
      </Stack>

      <Stack direction="row" gap="0.5rem" flexWrap="wrap">
        {dateRangeConfiguration.map(({ label, value, range }) => {
          const isSelectedRange = currentTo.isSame(range.endDate, 'day') && currentFrom.isSame(range.startDate, 'day');

          return (
            <Chip
              key={value}
              color={isSelectedRange ? 'primary' : 'default'}
              variant={isSelectedRange ? 'filled' : 'outlined'}
              label={<Typography variant="body2">{label}</Typography>}
              sx={{
                '&:hover': {
                  cursor: isSelectedRange ? 'not-allowed' : 'pointer',
                  ...(isSelectedRange && { backgroundColor: blue[600], boxShadow: 'none' }),
                },
              }}
              onClick={async () => {
                if (isSelectedRange) return;
                await onRangeChange(range);
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};
